import React from "react";
import { C, setTheme } from "../theme";
import Button from "./Button";
import SupportButton from "./SupportButton";

const resolveThemeMode = () => {
  if (typeof window === "undefined") return "dark";

  try {
    return window.localStorage.getItem("the99.themeMode") === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
};

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    setTheme(resolveThemeMode());
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error("[ErrorBoundary] Unhandled React render error:", error, errorInfo);
  }

  handleReset = () => {
    setTheme(resolveThemeMode());
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const message = this.state.error?.message || "Unexpected application error";

    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          padding: 24,
          background: C.appBackdrop,
          color: C.white,
          fontFamily: "Calibri,sans-serif",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 420,
            background: C.deepWater,
            border: `1px solid ${C.border}`,
            borderRadius: 28,
            boxShadow: C.frameShadow,
            padding: 24,
            display: "grid",
            gap: 14,
            textAlign: "left",
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 14,
              display: "grid",
              placeItems: "center",
              background: `${C.red}22`,
              color: C.red,
              fontSize: 22,
              fontWeight: 700,
            }}
          >
            !
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            <h1 style={{ margin: 0, fontSize: 24, lineHeight: 1.1 }}>The app hit a render error.</h1>
            <p style={{ margin: 0, color: C.muted, fontSize: 14, lineHeight: 1.5 }}>
              The current screen crashed before it could finish loading. Try rendering it again or reload the app.
            </p>
          </div>
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 16,
              background: C.cardBg,
              color: C.sand,
              fontSize: 12,
              lineHeight: 1.5,
              wordBreak: "break-word",
            }}
          >
            {message}
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Button variant="primary" onClick={this.handleReset}>
              Try again
            </Button>
            <Button variant="secondary" onClick={this.handleReload}>
              Reload app
            </Button>
            <SupportButton variant="inline" context="App crash" error={message} />
          </div>
        </div>
      </div>
    );
  }
}